import { useEffect, useState } from 'react';
import { apiService } from '../services/api';
import { UploadedDocument } from '../types';

const VALID_ID_TYPE = 'Valid ID';

// Mirrors maxProfileFilesFor on the server, which is what enforces it —
// this only lets the + button stop before the server would refuse.
function maxFilesFor(documentType: string): number {
  return documentType === VALID_ID_TYPE ? 5 : 10;
}

interface Props {
  onChange?: () => void;
  /**
   * Shows exactly these document types INSTEAD OF the global
   * admin-managed list this otherwise fetches for itself.
   *
   * The Apply dialog passes the scholarship's own required documents,
   * because that is what its gate enforces — listing the global six
   * under "All of these are required" would be untrue there, and an
   * upload row for a document the programme never asked for is noise.
   *
   * An empty array means the programme requires nothing, which is a real
   * answer and not a missing one.
   */
  requiredTypes?: string[];
}

/**
 * Uploads here are profile-level (applicationId omitted) — once uploaded,
 * they're reused across every scholarship application, no re-upload
 * needed. "Valid ID" itself is handled separately by ValidIdUpload,
 * inline in Section I of the profile form — excluded from this list so
 * it isn't shown twice.
 *
 * The list of required types is admin-managed (DocumentRequirementService)
 * rather than hardcoded — newly added requirements automatically get an
 * upload row here for any scholar who hasn't uploaded that type yet.
 */
const ProfileDocuments = ({ onChange, requiredTypes: overrideTypes }: Props) => {
  const [uploaded, setUploaded] = useState<UploadedDocument[]>([]);
  const [globalTypes, setGlobalTypes] = useState<string[]>([]);
  // Global requirements the admins marked optional — shown with an
  // "Optional" tag. Never applies to overrideTypes (a programme's own
  // list, every one of which its gate requires).
  const [optionalTypes, setOptionalTypes] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyKey, setBusyKey] = useState<string | null>(null);
  // Set only while an upload is waiting its turn in the server's upload
  // queue (see api.ts's waitForUploadSlot) — null means either idle or
  // actually uploading.
  const [queuePosition, setQueuePosition] = useState<number | null>(null);
  // "Uploading 2 of 3" while several files picked at once go up in turn.
  const [uploadProgress, setUploadProgress] = useState<{ done: number; total: number } | null>(null);

  const load = async () => {
    try {
      const [docs, requirements] = await Promise.all([
        apiService.getMyProfileDocuments(),
        apiService.getDocumentRequirements()
      ]);
      setUploaded(docs);
      setGlobalTypes(requirements.map((r) => r.documentType));
      setOptionalTypes(new Set(requirements.filter((r) => !r.isRequired).map((r) => r.documentType)));
    } catch (err: any) {
      setError(err.message || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Adds files under one document type — any number picked at once, up
  // to what's left of the per-type cap. Uploaded one after another, not
  // together: each takes its own turn in the server's upload queue, and
  // a failure part-way keeps the ones already done.
  const handleUpload = async (documentType: string, fileList: FileList | null, remaining: number) => {
    const files = Array.from(fileList ?? []);
    if (files.length === 0) return;
    setError('');
    const toUpload = files.slice(0, remaining);
    const skipped = files.length - toUpload.length;

    setBusyKey(`upload:${documentType}`);
    try {
      for (let i = 0; i < toUpload.length; i++) {
        setUploadProgress(toUpload.length > 1 ? { done: i, total: toUpload.length } : null);
        await apiService.uploadDocument(documentType, toUpload[i], undefined, (status) => setQueuePosition(status.position));
        setQueuePosition(null);
      }
      if (skipped > 0) {
        setError(
          `Only ${toUpload.length} of the ${files.length} files were added — ${documentType} allows ${maxFilesFor(documentType)} files.`
        );
      }
    } catch (err: any) {
      setError(err.message || 'Failed to upload document');
    } finally {
      await load();
      onChange?.();
      setBusyKey(null);
      setQueuePosition(null);
      setUploadProgress(null);
    }
  };

  const handleView = async (documentId: number) => {
    setBusyKey(`view:${documentId}`);
    setError('');
    try {
      const { blob, fileName } = await apiService.downloadDocument(documentId);
      const objectUrl = URL.createObjectURL(blob);
      // A direct <a download> click, not window.open(blobUrl, '_blank') —
      // mobile browsers (Android Chrome especially) block/ignore the popup
      // once the surrounding await has broken the user-activation window,
      // so nothing visibly happens. A forced download always works, and
      // the device's own viewer opens it from there.
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      // Revoked after a delay rather than immediately — some browsers need
      // a moment to actually pick up the blob URL before it's freed.
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    } catch (err: any) {
      setError(err.message || 'Failed to open document');
    } finally {
      setBusyKey(null);
    }
  };

  const handleDelete = async (documentId: number) => {
    setBusyKey(`delete:${documentId}`);
    setError('');
    try {
      await apiService.deleteDocument(documentId);
      await load();
      onChange?.();
    } catch (err: any) {
      setError(err.message || 'Failed to delete document');
    } finally {
      setBusyKey(null);
    }
  };

  if (loading) {
    return <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Loading documents...</p>;
  }

  // The caller's list wins outright when given — see the prop's note.
  const otherRequiredTypes = [...new Set(overrideTypes ?? globalTypes)].filter((t) => t !== VALID_ID_TYPE);

  return (
    <div>
      <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: 0, marginBottom: '0.6rem' }}>
        PDF, JPG, or PNG only, max 5MB per file. Images are automatically resized and compressed.
      </p>
      {error && <p style={{ color: 'var(--error-text)', fontSize: '0.8rem', marginBottom: '0.75rem' }}>{error}</p>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {otherRequiredTypes.map((documentType) => {
          const files = uploaded.filter((u) => u.documentType === documentType);
          const max = maxFilesFor(documentType);
          const remaining = max - files.length;
          const uploading = busyKey === `upload:${documentType}`;
          // Any other action on this type would race the upload — a "−"
          // mid-upload could free a slot the upload is about to take.
          const typeBusy = uploading || files.some((f) => busyKey === `delete:${f.id}`);

          return (
            <div
              key={documentType}
              style={{
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-md)',
                padding: '0.65rem 0.75rem',
                fontSize: '0.85rem'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}>
                <div>
                  <span style={{ fontWeight: 600 }}>{documentType}</span>
                  {!overrideTypes && optionalTypes.has(documentType) && (
                    <span className="badge badge-secondary" style={{ marginLeft: '0.4rem', fontSize: '0.7rem' }}>
                      Optional
                    </span>
                  )}
                  <span style={{ marginLeft: '0.4rem', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                    {files.length}/{max} file{max === 1 ? '' : 's'}
                  </span>
                </div>

                {/* "+" adds files — several can be picked at once. A label
                    wrapping a hidden input, so the whole button opens the
                    picker on every browser, phones included. */}
                <label
                  className="btn btn-outline btn-sm"
                  title={remaining > 0 ? `Add files to ${documentType}` : `${documentType} already has ${max} files`}
                  aria-label={`Add files to ${documentType}`}
                  style={{
                    cursor: remaining > 0 && !typeBusy ? 'pointer' : 'not-allowed',
                    opacity: remaining > 0 && !typeBusy ? 1 : 0.5,
                    margin: 0,
                    minWidth: '2.25rem',
                    justifyContent: 'center',
                    fontSize: uploading ? '0.8rem' : '1.1rem',
                    fontWeight: 700,
                    lineHeight: 1
                  }}
                >
                  {uploading
                    ? queuePosition !== null
                      ? `In line #${queuePosition}...`
                      : uploadProgress
                        ? `Uploading ${uploadProgress.done + 1} of ${uploadProgress.total}...`
                        : 'Uploading...'
                    : '+'}
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    multiple
                    style={{ display: 'none' }}
                    disabled={remaining <= 0 || typeBusy}
                    onChange={(e) => {
                      handleUpload(documentType, e.target.files, remaining);
                      // Cleared so picking the same file again still fires.
                      e.target.value = '';
                    }}
                  />
                </label>
              </div>

              {files.length === 0 ? (
                <p style={{ margin: '0.4rem 0 0', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                  No files yet — tap + to add one or more.
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', marginTop: '0.5rem' }}>
                  {files.map((file) => (
                    <div
                      key={file.id}
                      style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}
                    >
                      <span
                        style={{
                          fontSize: '0.78rem',
                          color: 'var(--text-secondary)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          minWidth: 0,
                          flex: 1
                        }}
                        title={file.fileName}
                      >
                        {file.fileName}
                      </span>
                      <span style={{ display: 'flex', gap: '0.4rem', flexShrink: 0 }}>
                        <button
                          className="btn btn-outline btn-sm"
                          disabled={busyKey === `view:${file.id}`}
                          onClick={() => handleView(file.id)}
                        >
                          {busyKey === `view:${file.id}` ? 'Opening...' : 'View'}
                        </button>
                        {/* "−" removes just this one file. */}
                        <button
                          className="btn btn-outline btn-sm"
                          title={`Remove ${file.fileName}`}
                          aria-label={`Remove ${file.fileName}`}
                          disabled={typeBusy}
                          onClick={() => {
                            // A small target on a phone, and removing deletes
                            // the file for good — so it asks first.
                            if (window.confirm(`Remove "${file.fileName}" from ${documentType}?`)) {
                              handleDelete(file.id);
                            }
                          }}
                          style={{
                            minWidth: '2.25rem',
                            justifyContent: 'center',
                            fontSize: '1.1rem',
                            fontWeight: 700,
                            lineHeight: 1,
                            color: 'var(--error-text)',
                            borderColor: 'var(--error-text)'
                          }}
                        >
                          {busyKey === `delete:${file.id}` ? '…' : '−'}
                        </button>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ProfileDocuments;
